<template>
  <div class="list-products">
    <KTCodePreview title="Danh sách cấu hình giá sản phẩm">
      <template v-slot:toolbar>
        <b-row>
          <b-dropdown size="sm" right>
            <template slot="button-content">
              <i style="font-size: 1rem" class="fas fa-cog"></i>
              <span class="font-weight-bolder">Thao tác</span>
            </template>
            <b-dropdown-item @click="openImportExportModal">
              <span>
                <i style="font-size: 1rem" class="fas fa-upload"></i>
                &nbsp; Import Excel</span
              >
            </b-dropdown-item>
            <b-dropdown-item @click="exportData">
              <span>
                <i style="font-size: 1rem" class="fas fa-file-excel"></i>
                &nbsp; Export Excel</span
              >
            </b-dropdown-item>
          </b-dropdown>
        </b-row>
      </template>
      <template v-slot:preview>
        <FilterWrapper :filters="filterFields" @filter="onFilterChange" />

        <b-table
          :items="products"
          :fields="tableFields"
          class="table-bordered table-hover"
          :small="true"
          :busy="loading"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="10" />
          </template>

          <template v-slot:cell(id)="row">
            <i
              class="fas fa-copy"
              style="cursor: pointer; font-size: 11px"
              v-clipboard:copy="row.item.id"
              title="Copy mã SP"
              @click="makeToastSuccess('Copy mã SP')"
            />
          </template>

          <template v-slot:cell(productType)="row">
            <div
              style="cursor: pointer"
              @click="findParentOrChild(row.item)"
              class="d-flex justify-content-center align-items-center"
            >
              <v-icon
                small
                style="font-size: 13px"
                v-b-tooltip
                :title="getProductTypeTooltip(row.item)"
              >
                {{ getProductTypeIcon(row.item) }}
              </v-icon>
            </div>
          </template>

          <template v-slot:cell(barCode)="row">
            <div v-if="row.item.barCode !== ''">
              <b v-text="row.item.barCode"></b>
              <p class="productCode mt-1 mb-0" @click="editProduct(row.item)">
                <span
                  v-text="
                    row.item.productCode.length > 15
                      ? row.item.productCode.slice(0, 16) + '...'
                      : row.item.productCode
                  "
                  style="cursor: pointer"
                ></span>
              </p>
            </div>
            <div v-else class="text-center">
              <span class="text-muted">-</span>
            </div>
          </template>

          <template v-slot:cell(productName)="row">
            <div>
              <span class="d-block white-space">
                {{ row.item.productName }}
              </span>
              <small v-if="row.item.productNameVat">
                <b class="text-warning">Tên VAT: </b>
                <b>{{ row.item.productNameVat }}</b>
              </small>
            </div>
          </template>

          <template v-slot:cell(stores)="row">
            <div
              v-b-tooltip.hover
              :title="getStoreListTooltip(row.item.stores)"
              class="text-center w-100 text-primary cursor-pointer"
            >
              <span class="store-count">
                {{ row.item.stores ? row.item.stores.length : 0 }}
              </span>
              Cửa hàng
              <i class="fas fa-store ml-1 text-primary" />
            </div>
          </template>

          <template v-slot:cell(sellingPrice)="row">
            <div
              @mouseover="hoverPrice = true"
              @mouseleave="hoverPrice = false"
              style="text-align: end; cursor: pointer"
              v-if="checkPermission('PRODUCT_ORIGINAL_PRICE_VIEW')"
            >
              <b-dropdown size="sm" no-caret right>
                <template slot="button-content">
                  <span v-text="convertPrice(row.item.sellingPrice)"></span>
                  <i
                    v-if="hoverPrice"
                    class="fas fa-edit ml-1"
                    style="font-size: 12px"
                  ></i>
                </template>
                <b-dropdown-item @click="handleShowPriceModel(row.item)">
                  <i class="fas fa-edit mr-2"></i>
                  Cập nhật giá chung
                </b-dropdown-item>
                <b-dropdown-item
                  @click="navigateToProductPriceConfig(row.item)"
                >
                  <i class="fas fa-store mr-2"></i>
                  Cập nhật giá theo cửa hàng
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>

          <template v-slot:cell(status)="row">
            <div class="text-center">
              <span
                class="label font-weight-bold label-lg label-inline"
                :class="getStatusClass(row.item.status)"
                style="width: max-content"
              >
                {{ getStatusName(row.item.status) }}
              </span>
            </div>
          </template>

          <template v-slot:cell(actions)="row">
            <div class="d-flex justify-content-center">
              <b-dropdown size="sm" no-caret right>
                <template slot="button-content">
                  <i
                    style="font-size: 1rem; padding-right: 0px"
                    class="flaticon2-settings"
                  />
                </template>
                <b-dropdown-item @click="editProduct(row.item)">
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem" class="flaticon-eye" />
                    &nbsp; Chi tiết sản phẩm
                  </span>
                </b-dropdown-item>
                <b-dropdown-item 
                  v-if="checkPermission('PRODUCT_ORIGINAL_PRICE_VIEW')"
                  @click="showDeletePriceConfigAlert(row.item)"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem; color: #d33" class="flaticon2-rubbish-bin-delete-button" />
                    &nbsp; Xóa cấu hình giá
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <b-row>
          <b-col>
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số: {{ convertPrice(totalItems) }}
            </p>
          </b-col>
          <b-col>
            <b-pagination-nav
              v-if="totalPages >= 2"
              :link-gen="linkGen"
              :number-of-pages="totalPages"
              use-router
              @change="fetchData"
              align="right"
              first-class="page-item-first btn btn-icon btn-sm m-1"
              prev-class="page-item-prev btn btn-icon btn-sm m-1"
              next-class="page-item-next btn btn-icon btn-sm m-1"
              last-class="page-item-last btn btn-icon btn-sm m-1"
              page-class="btn btn-icon btn-sm border-0 m-1"
            >
              <template v-slot:first-text>
                <span>
                  <i class="ki ki-double-arrow-back icon-xs" />
                </span>
              </template>
              <template v-slot:prev-text>
                <i class="ki ki-arrow-back icon-xs" />
              </template>
              <template v-slot:next-text>
                <i class="ki ki-arrow-next icon-xs" />
              </template>
              <template v-slot:last-text>
                <span class="text-info">
                  <i class="ki ki-double-arrow-next icon-xs" />
                </span>
              </template>
            </b-pagination-nav>
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>

    <ProductPriceImportExportModal
      :show="showImportExportModal"
      :loading="loading"
      :import-loading="importLoading"
      :preview-fields="previewFields"
      :import-data="importData"
      :has-import-errors="hasImportErrors"
      :total-errors="totalErrors"
      @close="closeImportExportModal"
      @download-template="downloadTemplate"
      @file-change="handleFileChange"
      @import="handleImport"
      @clear-data="clearImportData"
    />

    <!-- Modal cập nhật giá chung -->
    <b-modal v-model="showEditPriceModal" hide-footer :title="productName">
      <b-row class="mb-5">
        <b-col>
          <label class="labelInput" for="inputOriginalPrice">Giá nhập</label>
          <b-form-input
            v-model="originalPrice"
            size="sm"
            placeholder="Nhập giá nhập"
            v-mask="mask"
            disabled
          ></b-form-input>
        </b-col>
      </b-row>
      <b-row class="mb-5">
        <b-col>
          <label class="labelInput" for="inputSellingPrice">Giá bán</label>
          <b-form-input
            v-model="sellingPrice"
            size="sm"
            placeholder="Nhập giá bán"
            v-mask="mask"
          ></b-form-input>
        </b-col>
      </b-row>
      <b-row v-show="selectedProduct.parentProductId === null" class="mb-5">
        <b-col>
          <b-form-checkbox v-model="checkUpdate"
            >Cập nhật giá sản phẩm con theo cha</b-form-checkbox
          >
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            style="font-weight: 600; width: 70px"
            variant="primary"
            size="sm"
            @click="updateProduct"
            >Lưu</b-button
          >
          <b-button
            style="margin-left: 10px; font-weight: 600; width: 70px"
            variant="secondary"
            size="sm"
            @click="handleShowPriceModel"
            >Hủy</b-button
          >
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { VclTable } from 'vue-content-loading';
import {
  convertPrice,
  makeToastSuccess,
  makeToastWarning,
  makeToastFaile,
  currencyMask,
  unMaskPrice,
  handleError,
} from '@/utils/common';
import { showDeleteAlert } from '@/utils/sweet-alert2';
import { PRODUCT_STATUS, PRODUCT_TYPE } from '@/utils/enum';
import { PRODUCT_STATUS_NAME, PRODUCT_TYPE_NAME } from '@/utils/enum-name';
import FilterWrapper from '@/view/components/common/FilterWrapper.vue';
import ProductPriceImportExportModal from '@/view/components/products/ProductPriceImportExportModal.vue';
import * as XLSX from 'xlsx';
import localData from '@/utils/saveDataToLocal';

export default {
  name: 'ListProductsByPriceConfigs',
  components: {
    KTCodePreview,
    FilterWrapper,
    VclTable,
    ProductPriceImportExportModal,
  },
  data() {
    return {
      products: [],
      loading: false,
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 10,

      search: '',
      selectedCategories: [],
      selectedStatus: '',
      selectedProductType: '',
      parentProductId: '',
      childProductId: '',

      categoryOptions: [],
      statusOptions: this.generateOptions(
        PRODUCT_STATUS,
        PRODUCT_STATUS_NAME,
        'Chọn trạng thái',
      ),
      productTypeOptions: this.generateOptions(
        PRODUCT_TYPE,
        PRODUCT_TYPE_NAME,
        'Chọn thể loại',
      ),

      tableFields: [
        {
          key: 'id',
          label: 'ID',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'productType',
          label: 'Loại',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'barCode',
          label: 'Mã vạch',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'sellingPrice',
          label: 'Giá bán',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'stores',
          label: 'Cửa hàng',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],

      filterFields: [
        { key: 'search', type: 'string', placeholder: 'Tìm kiếm' },
        {
          key: 'category',
          type: 'custom-dropdown',
          placeholder: 'Danh mục sản phẩm',
          options: [],
          searchable: true,
          matchKey: ['name', 'shortName'],
          limit: 12,
        },
        {
          key: 'status',
          type: 'select',
          placeholder: 'Trạng thái sản phẩm',
          options: [],
        },
        {
          key: 'productType',
          type: 'select',
          placeholder: 'Thể loại',
          options: [],
        },
      ],

      showImportExportModal: false,
      selectedFile: null,
      importData: [],
      importLoading: false,

      showEditPriceModal: false,
      selectedProduct: {},
      productName: '',
      productId: '',
      originalPrice: '',
      sellingPrice: '',
      checkUpdate: false,
      hoverPrice: false,
      mask: currencyMask,

      previewFields: [
        { key: 'STT', label: 'STT' },
        { key: 'productCode', label: 'Mã sản phẩm' },
        { key: 'productName', label: 'Tên sản phẩm' },
        { key: 'storeId', label: 'Mã cửa hàng' },
        { key: 'storeName', label: 'Tên cửa hàng' },
        { key: 'sellingPrice', label: 'Giá bán lẻ' },
        { key: 'wholesalePrice', label: 'Giá bán buôn' },
        { key: 'listedPrice', label: 'Giá niêm yết' },
        { key: 'updateChildProducts', label: 'Cập nhật sản phẩm con' },
        { key: 'errors', label: 'Lỗi' },
      ],
      validationErrors: [],
      previewCurrentPage: 1,
      linkGen: (pageNum) => {
        return pageNum === 1 ? '?' : `?page=${pageNum}`;
      },
    };
  },
  mounted() {
    this.setBreadcrumb();
    this.initializeData();
    this.initializeFilterOptions();
  },
  methods: {
    convertPrice,
    makeToastSuccess,
    makeToastWarning,
    makeToastFaile,

    setBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Sản phẩm', route: '/products' },
        { title: 'Danh sách sản phẩm' },
      ]);
    },

    async initializeData() {
      this.currentPage = parseInt(this.$route.query.page) || 1;
      this.childProductId = this.$route.query.childProductId;

      await Promise.all([this.fetchCategories(), this.fetchData()]);
    },

    initializeFilterOptions() {
      this.updateFilterOptions('status', this.statusOptions);
      this.updateFilterOptions('productType', this.productTypeOptions);
    },

    async fetchData() {
      this.loading = true;
      try {
        this.currentPage = this.$route.query.page;
        if (!this.currentPage) {
          this.currentPage = 1;
        }

        const params = this.buildApiParams();
        const { data } = await ApiService.query(
          'product-price-configs/products',
          { params },
        );

        this.products = data.data.result.map((item, index) => ({
          count:
            this.currentPage >= 2
              ? index + 1 + this.currentPage * 10 - 10
              : index + 1,
          ...item,
        }));

        this.totalItems = data.data.totalItems;
        this.totalPages = data.data.totalPages;
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchCategories() {
      try {
        const params = {
          allMerchant: true,
        };

        const { data } = await ApiService.query('category', { params });
        this.categoryOptions = data.data.list_caterogy.map((element) => ({
          id: element.id,
          shortName: element.code,
          name: this.formatCategoryName(element),
        }));

        this.updateFilterOptions('category', this.categoryOptions);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    },

    buildApiParams() {
      const params = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      if (this.selectedCategories?.length > 0) {
        params.categoryIds = this.selectedCategories.map((item) => item.id);
      }

      if (this.search?.trim()) {
        params.name = this.search.trim();
      }

      if (this.selectedStatus) {
        params.status = this.selectedStatus;
      }

      if (this.selectedProductType) {
        params.productType = this.selectedProductType;
      }

      if (this.parentProductId) {
        params.parentProductId = this.parentProductId;
      }

      if (this.childProductId) {
        params.childProductId = this.childProductId;
      }

      return params;
    },

    generateOptions(enumObj, nameMapping, placeholder) {
      const options = [{ id: '', name: placeholder }];
      Object.values(enumObj).forEach((value) => {
        if (nameMapping[value]) {
          options.push({ id: value, name: nameMapping[value] });
        }
      });
      return options;
    },

    updateFilterOptions(filterKey, options) {
      const filterIndex = this.filterFields.findIndex(
        (filter) => filter.key === filterKey,
      );
      if (filterIndex !== -1) {
        this.$set(this.filterFields[filterIndex], 'options', options);
      }
    },

    formatCategoryName(element) {
      const prefix = '-'.repeat(element.level);
      return `${prefix}${element.name}`;
    },

    getProductTypeIcon(item) {
      const icons = {
        [PRODUCT_TYPE.PRODUCT]: 'mdi-star',
        [PRODUCT_TYPE.PRODUCT_IMEI]: 'fa-sitemap',
        [PRODUCT_TYPE.PRODUCT_COMBO]: 'fa-clipboard',
      };
      return icons[item.typeOfProduct] || 'mdi-star';
    },

    getProductTypeTooltip(item) {
      const tooltips = {
        [PRODUCT_TYPE.PRODUCT]: 'Sản phẩm độc lập',
        [PRODUCT_TYPE.PRODUCT_IMEI]: 'Danh sách sản phẩm con',
        [PRODUCT_TYPE.PRODUCT_COMBO]: 'Tới sản phẩm cha',
      };
      return tooltips[item.typeOfProduct] || 'Sản phẩm';
    },

    getStatusName(status) {
      return PRODUCT_STATUS_NAME[status] || '';
    },

    getStatusClass(status) {
      const classes = {
        [PRODUCT_STATUS.NEW]: 'label-light-warning',
        [PRODUCT_STATUS.SELLING]: 'label-light-success',
        [PRODUCT_STATUS.STOP_SELLING]: 'label-light-danger',
        [PRODUCT_STATUS.OUT_OF_STOCK]: 'label-light-default',
      };
      return classes[status] || '';
    },

    getStoreListTooltip(stores) {
      if (!stores || stores.length === 0) {
        return 'Không có cửa hàng nào';
      }
      return stores.map((store) => store.name).join(', ');
    },

    async downloadTemplate() {
      try {
        const response = await ApiService.query(
          'product-price-configs/template-imports',
          {
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'product-price-configs-template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.makeToastSuccess('Tải template thành công');
      } catch (error) {
        console.error('Error downloading template:', error);
        this.makeToastFaile('Lỗi khi tải template');
      }
    },

    openImportExportModal() {
      this.showImportExportModal = true;
      this.selectedFile = null;
      this.importData = [];
      this.validationErrors = [];
      this.previewCurrentPage = 1;
    },

    closeImportExportModal() {
      this.showImportExportModal = false;
      this.selectedFile = null;
      this.importData = [];
      this.validationErrors = [];
      this.previewCurrentPage = 1;
    },

    clearImportData() {
      this.importData = [];
      this.validationErrors = [];
      this.previewCurrentPage = 1;
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            this.makeToastFaile(
              'File không có dữ liệu. Vui lòng kiểm tra lại file Excel!',
            );
            this.importData = [];
            return;
          }

          const headers = jsonData[0];
          const dataRows = jsonData.slice(1).filter((row) => {
            return (
              row &&
              row.some(
                (cell) => cell !== null && cell !== undefined && cell !== '',
              )
            );
          });

          if (dataRows.length === 0) {
            this.makeToastFaile(
              'File không có dữ liệu hợp lệ. Vui lòng kiểm tra lại file Excel!',
            );
            this.importData = [];
            return;
          }

          const headerMap = {
            'Mã sản phẩm': 'productCode',
            'Tên sản phẩm': 'productName',
            'Mã cửa hàng': 'storeId',
            'Tên cửa hàng': 'storeName',
            'Giá bán lẻ': 'sellingPrice',
            'Giá bán buôn': 'wholesalePrice',
            'Giá niêm yết': 'listedPrice',
            'Cập nhật sản phẩm con': 'updateChildProducts',
          };

          this.importData = dataRows.map((row, index) => {
            const rowData = {};
            headers.forEach((header, headerIndex) => {
              const key = headerMap[header] || header;
              rowData[key] = row[headerIndex] || '';
            });
            rowData.STT = index + 1;
            return rowData;
          });

          this.validateImportData();
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          this.makeToastFaile('Lỗi khi đọc file Excel');
        }
      };
      reader.readAsArrayBuffer(file);
    },

    validateImportData() {
      this.importData.forEach((row) => {
        const rowErrors = [];

        if (!row.productCode) {
          rowErrors.push(`Thiếu Product Code`);
        } else if (typeof row.productCode !== 'string') {
          rowErrors.push(`Product Code phải là chuỗi`);
        }

        if (!row.productName) {
          rowErrors.push(`Thiếu Product Name`);
        }

        if (!row.storeId) {
          rowErrors.push(`Thiếu Store ID`);
        } else if (
          !Number.isInteger(Number(row.storeId)) ||
          Number(row.storeId) <= 0
        ) {
          rowErrors.push(`Store ID phải là số nguyên dương`);
        }

        if (!row.storeName) {
          rowErrors.push(`Thiếu Store Name`);
        }

        if (row.sellingPrice) {
          if (isNaN(Number(row.sellingPrice))) {
            rowErrors.push(`Giá bán lẻ phải là số`);
          } else if (Number(row.sellingPrice) < 0) {
            rowErrors.push(`Giá bán lẻ không được âm`);
          }
        }

        if (row.wholesalePrice) {
          if (isNaN(Number(row.wholesalePrice))) {
            rowErrors.push(`Giá bán buôn phải là số`);
          } else if (Number(row.wholesalePrice) < 0) {
            rowErrors.push(`Giá bán buôn không được âm`);
          }
        }

        if (row.listedPrice) {
          if (isNaN(Number(row.listedPrice))) {
            rowErrors.push(`Giá niêm yết phải là số`);
          } else if (Number(row.listedPrice) < 0) {
            rowErrors.push(`Giá niêm yết không được âm`);
          }
        }

        if (row.updateChildProducts !== undefined && row.updateChildProducts !== '') {
          const updateChildProductsValue = String(row.updateChildProducts).toLowerCase();
          if (updateChildProductsValue !== 'true' && updateChildProductsValue !== 'false' && 
              updateChildProductsValue !== '1' && updateChildProductsValue !== '0' &&
              updateChildProductsValue !== 'có' && updateChildProductsValue !== 'không' &&
              updateChildProductsValue !== 'yes' && updateChildProductsValue !== 'no') {
            rowErrors.push(`Cập nhật sản phẩm con phải là true/false, 1/0, có/không, hoặc yes/no`);
          }
        }

        this.$set(row, 'rowErrors', rowErrors);
      });
    },

    async handleImport() {
      const hasErrors = this.importData.some(
        (row) => row.rowErrors && row.rowErrors.length > 0,
      );
      if (hasErrors) {
        return;
      }

      // Hiển thị thông báo khi bắt đầu import
      this.makeToastWarning('Đang tiến hành import dữ liệu, vui lòng không đóng trình duyệt!');
      
      this.importLoading = true;
      try {
        const data = this.importData.map((row) => {
          let updateChildProducts = false;
          if (row.updateChildProducts !== undefined && row.updateChildProducts !== '') {
            const value = String(row.updateChildProducts).toLowerCase();
            updateChildProducts = value === 'true' || value === '1' || value === 'có' || value === 'yes';
          }

          return {
            productCode: row.productCode,
            storeId: Number(row.storeId),
            sellingPrice: row.sellingPrice ? Number(row.sellingPrice) : null,
            wholesalePrice: row.wholesalePrice
              ? Number(row.wholesalePrice)
              : null,
            listedPrice: row.listedPrice ? Number(row.listedPrice) : null,
            updateChildProducts: updateChildProducts,
          };
        });

        await ApiService.post('product-price-configs/imports', { data });

        this.makeToastSuccess('Import dữ liệu thành công! Dữ liệu đã được cập nhật vào hệ thống.');
        this.closeImportExportModal();
        this.fetchData();
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.data &&
          Array.isArray(error.response.data.data)
        ) {
          this.handleImportErrors(error.response.data.data);
          this.makeToastFaile(error.response.data.message);
        } else {
          this.makeToastFaile('Có lỗi xảy ra trong quá trình import. Vui lòng kiểm tra lại dữ liệu và thử lại!');
        }
      } finally {
        this.importLoading = false;
      }
    },

    handleImportErrors(serverErrors) {
      this.importData.forEach((row) => {
        const serverRow = serverErrors.find(
          (serverRow) =>
            serverRow.productCode === row.productCode &&
            serverRow.storeId === Number(row.storeId),
        );

        if (serverRow && serverRow.isError) {
          const existingErrors = row.rowErrors || [];
          const serverErrorMessages = serverRow.errors || [];
          this.$set(row, 'rowErrors', [
            ...existingErrors,
            ...serverErrorMessages,
          ]);
          this.$set(row, 'isError', true);
        } else if (serverRow && !serverRow.isError) {
          this.$set(row, 'rowErrors', []);
          this.$set(row, 'isError', false);
        }
      });
    },

    async exportData() {
      try {
        const params = this.buildApiParams();
        const response = await ApiService.query(
          'product-price-configs/export-excels',
          {
            params,
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'product-price-configs-export.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.makeToastSuccess('Export thành công');
      } catch (error) {
        console.error('Error exporting data:', error);
        this.makeToastFaile('Lỗi khi export dữ liệu');
      }
    },

    onFilterChange(filterValues) {
      this.search = filterValues.search || '';
      this.selectedCategories = filterValues.category || [];
      this.selectedStatus = filterValues.status || '';
      this.selectedProductType = filterValues.productType || '';

      this.currentPage = 1;
      this.fetchData();
    },

    findParentOrChild(item) {
      this.search = '';
      this.selectedStatus = '';
      this.selectedProductType = '';

      if (item.typeOfProduct === PRODUCT_TYPE.PRODUCT_COMBO) {
        this.childProductId = '';
        this.parentProductId = item.parentProductId;
      } else if (item.typeOfProduct === PRODUCT_TYPE.PRODUCT_IMEI) {
        this.parentProductId = '';
        this.childProductId = item.id;
      }

      this.fetchData();
    },

    editProduct(item) {
      const route = this.$router.resolve({
        name: 'update-product',
        query: {
          id: item.id,
          fromProductPriceConfig: 'true',
        },
      });
      window.open(route.href, '_blank');
    },

    // Methods cho modal cập nhật giá
    handleShowPriceModel(item) {
      this.showEditPriceModal = !this.showEditPriceModal;
      this.selectedProduct = item;
      this.productId = item.id;
      this.productName = item.productName;
      this.originalPrice = item.originalPrice;
      this.sellingPrice = item.sellingPrice;
    },

    navigateToProductPriceConfig(item) {
      this.$router.push({
        name: 'update-product',
        query: {
          id: item.id,
          fromProductPriceConfig: 'true',
        },
      });
    },

    async updateProduct() {
      const data = {
        id: this.productId,
        originalPrice: this.originalPrice ? unMaskPrice(this.originalPrice) : 0,
        sellingPrice: this.sellingPrice ? unMaskPrice(this.sellingPrice) : 0,
        isUpdateChild: this.checkUpdate,
      };

      try {
        const response = await ApiService.put('product/update-price', data);
        const { message } = response.data;

        this.fetchData();
        this.showEditPriceModal = false;
        this.makeToastSuccess(message);
      } catch (error) {
        handleError(error, 'Có lỗi xảy ra khi cập nhật giá!');
      }
    },

    showDeletePriceConfigAlert(item) {
      showDeleteAlert(
        'Xóa cấu hình giá!',
        `Bạn có chắc muốn xóa tất cả cấu hình giá cho sản phẩm "${item.productName}"?`,
        item,
        this.deletePriceConfig,
      );
    },

    async deletePriceConfig(item) {
      try {
        await ApiService.delete(`/product-price-configs/${item.id}`);
        this.makeToastSuccess('Xóa cấu hình giá thành công');
        this.fetchData();
      } catch (error) {
        console.error('Error deleting price config:', error);
        this.makeToastFaile('Không thể xóa cấu hình giá');
      }
    },

    checkPermission(permission) {
      return localData.checkPermission(permission);
    },
  },
  computed: {
    paginatedImportData() {
      const start = (this.previewCurrentPage - 1) * 10;
      const end = start + 10;
      return this.importData.slice(start, end);
    },
    totalErrors() {
      return this.importData.filter(
        (row) => row.rowErrors && row.rowErrors.length > 0,
      ).length;
    },
    hasImportErrors() {
      return this.importData.some(
        (row) => row.rowErrors && row.rowErrors.length > 0,
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.productCode:hover {
  text-decoration: underline;
}

.table td {
  vertical-align: middle;
}

.labelInput {
  font-weight: 600;
}
</style>
